import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Radio, Eye } from 'lucide-react';

const SimplifiedModuleDiagram = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [activeZone, setActiveZone] = useState(null);

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setActiveZone(null);
      }}
    >
      <div className="relative rounded-3xl overflow-hidden bg-gradient-to-b from-gray-800/30 to-gray-900/50 border border-gray-800/50 p-8 md:p-12">
        {/* Background glow on hover */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5"
          animate={{ opacity: isHovered ? 1 : 0 }}
          transition={{ duration: 0.3 }}
        />

        <div className="grid lg:grid-cols-2 gap-8 items-center relative z-10">
          {/* SVG Visualization */}
          <div className="relative aspect-square max-w-sm mx-auto">
            <svg viewBox="0 0 300 400" className="w-full h-full">
              <defs>
                <linearGradient id="stickGradSimple" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#4a4a4a" />
                  <stop offset="100%" stopColor="#2a2a2a" />
                </linearGradient>
                <linearGradient id="moduleGradSimple" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#3a3a3a" />
                  <stop offset="100%" stopColor="#1a1a1a" />
                </linearGradient>
                <linearGradient id="ultrasonicGradSimple" x1="0%" y1="100%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="rgba(59, 130, 246, 0.5)" />
                  <stop offset="100%" stopColor="rgba(59, 130, 246, 0)" />
                </linearGradient>
                <linearGradient id="irGradSimple" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="rgba(168, 85, 247, 0.5)" />
                  <stop offset="100%" stopColor="rgba(168, 85, 247, 0)" />
                </linearGradient>
                <filter id="glowSimple">
                  <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                  <feMerge>
                    <feMergeNode in="coloredBlur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              {/* Walking Stick */}
              <rect x="142" y="70" width="16" height="300" rx="8" fill="url(#stickGradSimple)" />
              <ellipse cx="150" cy="370" rx="12" ry="6" fill="#1a1a1a" />

              {/* Module Housing */}
              <rect x="125" y="100" width="50" height="65" rx="8" fill="url(#moduleGradSimple)" stroke="#4a4a4a" strokeWidth="1" />
              
              {/* Mounting Collar */}
              <ellipse cx="150" cy="165" rx="22" ry="6" fill="#2a2a2a" stroke="#3a3a3a" strokeWidth="1" />
              <rect x="128" y="158" width="44" height="14" fill="#2a2a2a" />
              <ellipse cx="150" cy="172" rx="22" ry="6" fill="#2a2a2a" stroke="#3a3a3a" strokeWidth="1" />

              {/* Ultrasonic Sensors */}
              <g
                onMouseEnter={() => setActiveZone('ultrasonic')}
                onMouseLeave={() => setActiveZone(null)}
                className="cursor-pointer"
              >
                <circle cx="140" cy="118" r="6" fill="#1a1a1a" stroke={activeZone === 'ultrasonic' || isHovered ? '#3b82f6' : '#3a3a3a'} strokeWidth="1.5" />
                <circle cx="160" cy="118" r="6" fill="#1a1a1a" stroke={activeZone === 'ultrasonic' || isHovered ? '#3b82f6' : '#3a3a3a'} strokeWidth="1.5" />
                <motion.circle
                  cx="140"
                  cy="118"
                  r="2"
                  fill="#3b82f6"
                  filter="url(#glowSimple)"
                  animate={{ opacity: isHovered ? [0.3, 1, 0.3] : 0.3 }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                />
                <motion.circle
                  cx="160"
                  cy="118"
                  r="2"
                  fill="#3b82f6"
                  filter="url(#glowSimple)"
                  animate={{ opacity: isHovered ? [0.3, 1, 0.3] : 0.3 }}
                  transition={{ duration: 1.5, repeat: Infinity, delay: 0.3 }}
                />
              </g>

              {/* IR Sensor */}
              <g
                onMouseEnter={() => setActiveZone('ir')}
                onMouseLeave={() => setActiveZone(null)}
                className="cursor-pointer"
              >
                <rect x="135" y="148" width="30" height="8" rx="2" fill="#1a1a1a" stroke={activeZone === 'ir' || isHovered ? '#a855f7' : '#3a3a3a'} strokeWidth="1.5" />
                <motion.rect
                  x="140"
                  y="150"
                  width="20"
                  height="4"
                  rx="1"
                  fill="#a855f7"
                  filter="url(#glowSimple)"
                  animate={{ opacity: isHovered ? [0.2, 0.7, 0.2] : 0.2 }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              </g>

              {/* Status LED */}
              <motion.circle
                cx="150"
                cy="135"
                r="3"
                fill="#10b981"
                filter="url(#glowSimple)"
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 2, repeat: Infinity }}
              />

              {/* Ultrasonic Detection Visualization */}
              <motion.g
                animate={{ opacity: isHovered || activeZone === 'ultrasonic' ? 1 : 0 }}
                transition={{ duration: 0.3 }}
              >
                {[1, 2, 3].map((i) => (
                  <motion.path
                    key={`arc-${i}`}
                    d={`M ${100 - i * 15} ${100 - i * 10} Q 150 ${70 - i * 15} ${200 + i * 15} ${100 - i * 10}`}
                    fill="none"
                    stroke="rgba(59, 130, 246, 0.4)"
                    strokeWidth="1.5"
                    animate={{
                      opacity: [0.1, 0.5, 0.1],
                    }}
                    transition={{ duration: 2, repeat: Infinity, delay: i * 0.2 }}
                  />
                ))}
              </motion.g>

              {/* IR Ground Detection Visualization */}
              <motion.g
                animate={{ opacity: isHovered || activeZone === 'ir' ? 1 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <motion.polygon
                  points="150,175 120,320 180,320"
                  fill="url(#irGradSimple)"
                  animate={{ opacity: [0.2, 0.5, 0.2] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
                <motion.ellipse
                  cx="150"
                  cy="320"
                  rx="35"
                  ry="8"
                  fill="rgba(168, 85, 247, 0.2)"
                  stroke="rgba(168, 85, 247, 0.4)"
                  strokeWidth="1"
                  animate={{ rx: [35, 40, 35], opacity: [0.2, 0.4, 0.2] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              </motion.g>
            </svg>
          </div>

          {/* Info Panel */}
          <div className="space-y-6">
            <div>
              <h3 className="text-2xl md:text-3xl font-semibold mb-3">Dual-Zone Detection</h3>
              <p className="text-gray-400 leading-relaxed">
                ClearWay&apos;s compact module integrates two sensor systems for comprehensive 
                awareness — detecting both obstacles ahead and hazards underfoot.
              </p>
            </div>

            <div className="space-y-4">
              <motion.div
                className={`p-4 rounded-xl border transition-all cursor-pointer ${
                  activeZone === 'ultrasonic'
                    ? 'bg-blue-500/10 border-blue-500/40'
                    : 'bg-gray-800/30 border-gray-700/50 hover:border-blue-500/30'
                }`}
                onMouseEnter={() => setActiveZone('ultrasonic')}
                onMouseLeave={() => setActiveZone(null)}
              >
                <div className="flex items-center gap-3 mb-2">
                  <Radio className="w-5 h-5 text-blue-400" />
                  <span className="font-medium">Forward Ultrasonic</span>
                </div>
                <p className="text-gray-500 text-sm">Detects obstacles, walls, and objects in your path</p>
              </motion.div>

              <motion.div
                className={`p-4 rounded-xl border transition-all cursor-pointer ${
                  activeZone === 'ir'
                    ? 'bg-purple-500/10 border-purple-500/40'
                    : 'bg-gray-800/30 border-gray-700/50 hover:border-purple-500/30'
                }`}
                onMouseEnter={() => setActiveZone('ir')}
                onMouseLeave={() => setActiveZone(null)}
              >
                <div className="flex items-center gap-3 mb-2">
                  <Eye className="w-5 h-5 text-purple-400" />
                  <span className="font-medium">Ground IR Scanner</span>
                </div>
                <p className="text-gray-500 text-sm">Identifies potholes, drops, steps, and surface changes</p>
              </motion.div>
            </div>

            <Link
              to="/technology"
              className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 font-medium transition-colors group"
            >
              Explore the technology
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>

        {/* Disclosure */}
        <p className="text-xs text-gray-600 text-center mt-6">
          * Visualization is representative. Actual product design may vary.
        </p>
      </div>
    </motion.div>
  );
};

export default SimplifiedModuleDiagram;
